import { queryKeys } from '@/lib/query-keys';
import { apiClient } from '@borradh-workspace/api-client';
import type { StripeConnectStatus } from '@borradh-workspace/api-client/types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { getAccountStatusQueryOptions } from '../get-account-status';

/**
 * Force a live sync of the connected account from Stripe and prime the
 * account-status cache with the result. Used when the user returns from
 * hosted onboarding, before Stripe's account.updated webhook has arrived.
 *
 * Then invalidate every Stripe query. The service dialogs decide whether a
 * deposit can be required from `['integrations', 'stripe']`, cached for five
 * minutes; left alone it kept saying "can't charge" (or "can") long after
 * this refresh had learned otherwise. Same as `useLinkStripeAccount`.
 */
export const useRefreshAccountStatus = () => {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: () =>
      apiClient.post<StripeConnectStatus>(
        'integrations/stripe/account-refresh',
        {}
      ),
    onSuccess: (status) => {
      queryClient.setQueryData(getAccountStatusQueryOptions().queryKey, status);
      void queryClient.invalidateQueries({
        queryKey: queryKeys.integrations.stripe(),
      });
    },
  });

  return {
    refreshAccountStatus: mutation.mutate,
    refreshAccountStatusAsync: mutation.mutateAsync,
    isRefreshing: mutation.isPending,
  };
};
